// ProductQuantityInput - Heavy inventory validation for product pages only
// This file contains the complete inventory validation feature with encryption,
// caching, and real-time updates for product pages.

if (!customElements.get('product-quantity-input')) {
  class ProductQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.input.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
      
      // Obfuscated cache variables for scale-based caching
      this._cachedScale = null;
      this._cachedMap = null;
      this._lastScaleCheck = 0;
      this._scaleRefreshInterval = 10 * 60 * 1000; // 10 minutes (increased since no string replacement)
      
      // Obfuscated cache variables for DOM element caching
      this._domCache = {
        addToCartButton: null,
        inventoryMessage: null,
        atcWrapper: null,
        buttonPlus: null,
        buttonMinus: null,
        lastUpdate: 0,
        expiry: 10 * 60 * 1000 // 10 minutes (increased since no string replacement)
      };
      
      // Debouncing variable
      this._validationTimeout = null;
      
      // Pre-computed messages for maximum performance
      this._cartEmptyMessage = "You've reached the maximum order limit for this product. Please reduce the quantity to add it to your cart. If you would like to order more, kindly contact our sales team.";
      this._cartPartialMessage = "You've reached the maximum order limit for this product in your cart. Please reduce the quantity to add more. If you would like to order more, kindly contact our sales team.";
      this._cartMaxedMessage = "You've already added the maximum allowed quantity of this product to your cart. To order more, please contact our sales team.";
      
      // Cache pre-built DOM elements for performance
      this._inventoryMessageElement = document.querySelector('.inventory-message');
    }

    quantityUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;

    connectedCallback() {
      // Auto-detect if this is a product page quantity input by checking for product scale data
      const hasProductScale = this.input.dataset.productScale;
      this._isProductPageQuantityInput = !!hasProductScale;
      
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      
      // Listen for variant changes to update inventory quantity
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.updateProductMap(event.data.variant);
        this._clearDomCache(); // Clear DOM cache on variant change
        
        // Re-validate if this is a product page quantity input
        if (this._isProductPageQuantityInput) {
          this.validateQtyRules();
        }
      });
      
      // All quantity inputs sync cart data, but only product page ones validate
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this._isProductPageQuantityInput) {
          // Full update with validation and reset for product form submissions
          this.updateCartQuantity(event.cartData, event.source === 'product-form');
          this._clearDomCache(); // Clear DOM cache on cart update
        } else {
          // Lightweight update for non-product contexts
          this.updateCartQuantityDataOnly(event.cartData, event.source === 'product-form');
        }
      });
    }

    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      
      // Clear any pending validation timeout
      if (this._validationTimeout) {
        clearTimeout(this._validationTimeout);
      }
    }

    onInputChange(event) {
      // Debounce validation to prevent excessive calls
      this._debouncedValidateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;

      if (event.target.name === 'plus') {
        if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
          this.input.value = this.input.dataset.min;
        } else {
          this.input.stepUp();
        }
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }

      if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
        this.input.value = parseInt(this.input.min);
      }
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      
      // Check minimum quantity
      if (this.input.min) {
        const buttonMinus = this._buttonMinus;
        if (buttonMinus) {
          buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
      }
      
      // Check maximum quantity
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const buttonPlus = this._buttonPlus;
        if (buttonPlus) {
          buttonPlus.classList.toggle('disabled', value >= max);
        }
      }
      
      // Check product rating (new validation)
      this.validateProductMap(value);
    }

    validateProductMap(selectedQuantity) {
      const productScale = this.input.dataset.productScale;
      
      // Only validate if product scale is available
      if (!productScale) return;
      
      // Get map value (cached or fresh)
      const map = this._getMapValue(productScale);
      
      // Skip validation if inventory is 0 - this case is handled by the sold-out button logic
      if (map === 0) return;
      
      // Use cart quantity cache and selected quantity parameter directly
      const cartQuantity = this._getParsedCartQuantity();
      const totalQuantity = cartQuantity + selectedQuantity;
      const exceedsMap = totalQuantity > map;
      
      
      // Disable plus button if adding one more would exceed map
      const buttonPlus = this._buttonPlus;
      if (buttonPlus) {
        const wouldExceedWithOne = cartQuantity + selectedQuantity + 1 > map;
        buttonPlus.classList.toggle('disabled', wouldExceedWithOne);
      }
      
      // Find and update the add to cart button and message
      this.updateAddToCartButton(exceedsMap);
    }

    updateAddToCartButton(exceedsInventory) {
      // Use cached DOM elements
      const addToCartButton = this._addToCartButton;
      if (!addToCartButton) {
        console.warn('ProductQuantityInput: Add to cart button not found');
        return;
      }
      
      const inventoryMessage = this._inventoryMessage;
      
      // CRITICAL: Immediate state updates (for logic and accessibility)
      if (exceedsInventory) {
        addToCartButton.disabled = true; // ✅ Logic works immediately
        addToCartButton.setAttribute('aria-disabled', 'true'); // ✅ Accessibility works immediately
      } else {
        addToCartButton.disabled = false; // ✅ Logic works immediately
        addToCartButton.removeAttribute('aria-disabled'); // ✅ Accessibility works immediately
      }
      
      // VISUAL: Deferred styling updates (for smooth animations)
      requestAnimationFrame(() => {
        if (exceedsInventory) {
          addToCartButton.classList.add('inventory-disabled'); // 🎨 Smooth visual
          const message = this.generateInventoryMessage();
          inventoryMessage.textContent = message;
          this.showInventoryMessage(inventoryMessage); // 🎨 Smooth animation
          inventoryMessage.classList.add('inventory-error'); // 🎨 Smooth visual
          inventoryMessage.setAttribute('role', 'alert'); // 🎨 Accessibility
        } else {
          addToCartButton.classList.remove('inventory-disabled'); // 🎨 Smooth visual
          this.hideInventoryMessage(inventoryMessage); // 🎨 Smooth animation
          inventoryMessage.classList.remove('inventory-error'); // 🎨 Smooth visual
          inventoryMessage.removeAttribute('role'); // 🎨 Accessibility
        }
      });
    }

    _findInventoryMessage() {
      // Use cached pre-built DOM element for performance
      return this._inventoryMessageElement;
    }

    showInventoryMessage(message) {
      // Use CSS classes for smooth animations (no inline styles)
      message.classList.remove('hide');
      message.classList.add('show');
    }

    hideInventoryMessage(message) {
      // Use CSS classes for smooth animations (no inline styles)
      message.classList.remove('show');
      message.classList.add('hide');
    }

    generateInventoryMessage() {
      const productScale = this.input.dataset.productScale;
      if (!productScale) return '';
      
      const map = this._getMapValue(productScale);
      const cartQuantity = this._getParsedCartQuantity();
      
      if (cartQuantity === 0) {
        // Cart Empty scenario
        return this._cartEmptyMessage;
      } else if (cartQuantity > 0 && cartQuantity < map) {
        // Cart Partial scenario
        return this._cartPartialMessage;
      } else if (cartQuantity >= map) {
        // Cart Already maxed scenario
        return this._cartMaxedMessage;
      }
      
      // Fallback
      return this._cartEmptyMessage;
    }

    _getMapValue(productScale) {
      // Check if we need to refresh map value
      const now = Date.now();
      const shouldRefresh = this._shouldRefreshMap(now);
      
      // Use cache if scale hasn't changed and not forcing refresh
      if (!shouldRefresh && this._cachedScale === productScale && this._cachedMap !== null) {
        return this._cachedMap;
      }
      
      // Decrypt and cache
      this._cachedScale = productScale;
      this._cachedMap = this.decryptProductMap(productScale);
      this._lastScaleCheck = now;
      
      return this._cachedMap;
    }
    
    _shouldRefreshMap(now) {
      return (now - this._lastScaleCheck) > this._scaleRefreshInterval;
    }
    
    _clearDomCache() {
      this._domCache.addToCartButton = null;
      this._domCache.inventoryMessage = null;
      this._domCache.atcWrapper = null;
      this._domCache.buttonPlus = null;
      this._domCache.buttonMinus = null;
    }
    
    _isElementValid(element) {
      return element && document.contains(element);
    }
    
    _shouldRefreshDomCache(now) {
      return (now - this._domCache.lastUpdate) > this._domCache.expiry;
    }
    
    _debouncedValidateQtyRules() {
      // Clear existing timeout
      if (this._validationTimeout) {
        clearTimeout(this._validationTimeout);
      }
      
      // Set new timeout for debounced validation (reduced to 50ms for better responsiveness)
      this._validationTimeout = setTimeout(() => {
        this.validateQtyRules();
      }, 50);
    }
    
    _getProductMap() {
      // Extract key from concatenated product scale value
      const productScale = this.input.dataset.productScale;
      
      if (!productScale) {
        return 0;
      }
      
      // Split by ':' separator to get key (second part)
      const parts = productScale.split(':');
      if (parts.length !== 2) {
        return 0;
      }
      
      return parseInt(parts[1]); // Return the key part
    }
    
    get _addToCartButton() {
      const now = Date.now();
      const shouldRefresh = this._shouldRefreshDomCache(now);
      
      // Use cache if element is valid and not expired
      if (!shouldRefresh && this._isElementValid(this._domCache.addToCartButton)) {
        return this._domCache.addToCartButton;
      }
      
      // Re-query and cache
      const formId = this.input.getAttribute('form');
      if (!formId) {
        console.warn('ProductQuantityInput: No form attribute found, inventory validation disabled');
        return null;
      }
      
      const actualForm = document.getElementById(formId);
      if (!actualForm) {
        console.warn(`ProductQuantityInput: Form with id "${formId}" not found, inventory validation disabled`);
        return null;
      }
      
      this._domCache.addToCartButton = actualForm.querySelector('[type="submit"][name="add"]');
      this._domCache.lastUpdate = now;
      
      return this._domCache.addToCartButton;
    }
    
    get _inventoryMessage() {
      const now = Date.now();
      const shouldRefresh = this._shouldRefreshDomCache(now);
      
      // Use cache if element is valid and not expired
      if (!shouldRefresh && this._isElementValid(this._domCache.inventoryMessage)) {
        return this._domCache.inventoryMessage;
      }
      
      // Re-query and cache
      this._domCache.inventoryMessage = this._findInventoryMessage();
      this._domCache.lastUpdate = now;
      
      return this._domCache.inventoryMessage;
    }
    
    get _buttonPlus() {
      const now = Date.now();
      const shouldRefresh = this._shouldRefreshDomCache(now);
      
      // Use cache if element is valid and not expired
      if (!shouldRefresh && this._isElementValid(this._domCache.buttonPlus)) {
        return this._domCache.buttonPlus;
      }
      
      // Re-query and cache
      this._domCache.buttonPlus = this.querySelector(".quantity__button[name='plus']");
      this._domCache.lastUpdate = now;
      
      return this._domCache.buttonPlus;
    }
    
    get _buttonMinus() {
      const now = Date.now();
      const shouldRefresh = this._shouldRefreshDomCache(now);
      
      // Use cache if element is valid and not expired
      if (!shouldRefresh && this._isElementValid(this._domCache.buttonMinus)) {
        return this._domCache.buttonMinus;
      }
      
      // Re-query and cache
      this._domCache.buttonMinus = this.querySelector(".quantity__button[name='minus']");
      this._domCache.lastUpdate = now;
      
      return this._domCache.buttonMinus;
    }
    
    get _parsedCartQuantity() {
      // Cache parsed cart quantity to avoid repeated parseInt() calls
      if (this._cachedCartQuantity === undefined) {
        this._cachedCartQuantity = parseInt(this.input.dataset.cartQuantity) || 0;
      }
      return this._cachedCartQuantity;
    }
    
    _getParsedCartQuantity() {
      // Clear cache when cart quantity might have changed
      this._cachedCartQuantity = undefined;
      return this._parsedCartQuantity;
    }

    decryptProductMap(productScale) {
      // Split the concatenated value to get encrypted inventory and key
      const parts = productScale.split(':');
      if (parts.length !== 2) {
        console.warn('Invalid product scale format:', productScale);
        return 0;
      }
      
      const encryptedNum = parseInt(parts[0]); // Encrypted inventory
      const keyNum = parseInt(parts[1]);       // Key
      
      // Decrypt: reverse the server-side encryption
      const decrypted = ((encryptedNum * 923077) % 1000000) - keyNum;
      const finalResult = Math.max(0, decrypted);

      return finalResult;
    }

    updateProductMap(variant) {
      // Update the encrypted product rating when variant changes
      if (variant && variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
        // Use simple variant ID as key (matching server logic)
        const productMap = variant.id % 1000000;
        const encrypted = (variant.inventory_quantity + productMap) * 13 % 1000000;
        
        const newScale = encrypted + ':' + productMap;
        
        this.input.dataset.productScale = newScale;
        this.input.dataset.variantSku = variant.sku;
        
        // Clear cache when variant changes (new scale)
        this._cachedScale = null;
        this._cachedMap = null;
      } else {
        // Remove product scale if not tracked
        delete this.input.dataset.productScale;
        delete this.input.dataset.variantSku;
        
        // Clear cache when no scale available
        this._cachedScale = null;
        this._cachedMap = null;
      }
      
      // Re-validate with new product rating
      this.validateQtyRules();
    }
    
    updateCartQuantity(cartData, shouldResetInput = false) {
      
      // Update cart quantity when cart changes
      if (!cartData || !cartData.items) {

        // Fallback: fetch current cart state
        this.fetchAndUpdateCartQuantity(shouldResetInput);
        return;
      }
      
      // Process the valid cart data
      this.processCartData(cartData, shouldResetInput);
    }
    
    updateCartQuantityDataOnly(cartData, shouldResetInput = false) {
      // Lightweight cart data sync - only update the data attribute, no validation
      if (!cartData || !cartData.items) {
        this.input.dataset.cartQuantity = '0';
        return;
      }
      
      // Get current variant ID from the input
      const currentVariantId = this.getCurrentVariantId();
      if (!currentVariantId) {
        return;
      }

      // Find the current variant in cart items
      let cartQuantity = 0;
      for (const item of cartData.items) {
        if (item.variant_id.toString() === currentVariantId.toString()) {
          cartQuantity += item.quantity;
        }
      }

      // Update the data attribute for accurate validation when needed
      this.input.dataset.cartQuantity = cartQuantity.toString();
      
      // Clear parseInt cache when cart quantity changes
      this._cachedCartQuantity = undefined;

      // Only reset quantity selector for add-to-cart actions from product page
      if (shouldResetInput) {
        this.resetQuantityToMinimum();
      }
    }
    
    getCurrentVariantId() {
      // Method 1: Get from the connected form's variant input
      const formId = this.input.getAttribute('form');
      if (formId) {
        const connectedForm = document.getElementById(formId);
        if (connectedForm) {
          const variantInput = connectedForm.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            return variantInput.value;
          }
        }
      }
      
      // Method 2: Fallback to any variant input on the page
      const variantInput = document.querySelector('input[name="id"].product-variant-id');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
      
      // Method 3: URL parameter fallback
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('variant') || null;
    }

    resetQuantityToMinimum() {
      // Get the minimum allowed quantity - ensure it's always a valid positive integer
      const minAttr = this.input.getAttribute('min') || this.input.dataset.min || '1';
      const minInt = Math.max(1, parseInt(minAttr) || 1); // Ensure minimum is at least 1
      const minValue = minInt.toString(); // Convert back to string for consistency
      
      const currentValue = parseInt(this.input.value) || 1;
      
      
      // Only reset if current value is greater than minimum (indicating a successful add to cart)
      if (currentValue > minInt) {
        this.input.value = minValue;
        
        // Trigger change event without bubbling to prevent interference with cart.js
        this.input.dispatchEvent(new Event('change', { bubbles: false }));
      }
    }

    async fetchAndUpdateCartQuantity(shouldResetInput = false) {
      try {
        const response = await fetch(window.routes.cart_url + '.js');
        
        if (!response.ok) {
          throw new Error(`Cart fetch failed: ${response.status}`);
        }
        
        const cartData = await response.json();
        // Process the fetched cart data (without triggering another fetch)
        this.processCartData(cartData, shouldResetInput);
        
      } catch (error) {
        // Fallback: set cart quantity to 0 if fetch fails
        this.input.dataset.cartQuantity = '0';
        this._cachedCartQuantity = undefined; // Clear cache
        // Validate with fallback data
        this.validateQtyRules();
      }
    }

    processCartData(cartData, shouldResetInput = false) {

      if (!cartData || !cartData.items) {
        // If still no cart data, set cart quantity to 0
        this.input.dataset.cartQuantity = '0';
        // Note: No automatic validation - only when user explicitly interacts
        return;
      }

      // Get current variant ID from the input
      const currentVariantId = this.getCurrentVariantId();
      if (!currentVariantId) {
        return;
      }

      // Find the current variant in cart items
      let cartQuantity = 0;
      for (const item of cartData.items) {
        if (item.variant_id.toString() === currentVariantId.toString()) {
          cartQuantity += item.quantity;
        }
      }

      // Always update the data attribute for accurate validation
      this.input.dataset.cartQuantity = cartQuantity.toString();
      
      // Clear parseInt cache when cart quantity changes
      this._cachedCartQuantity = undefined;

      // Only reset quantity selector for add-to-cart actions from product page
      if (shouldResetInput) {
        this.resetQuantityToMinimum();
      }

      // CRITICAL: Re-validate after cart data changes to update button states and messages
      this.validateQtyRules();
    }
  }

  customElements.define('product-quantity-input', ProductQuantityInput);
}
